import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';

import { IRootState } from 'Scripts/store';

import {
    useAddDiscountMutation,
    useAddToCartMutation,
    useGetCartQuery,
    useLoadOrderIntoCartMutation,
    useUnloadOrderFromCartMutation,
    useReplaceCartMutation,
    useUpdateCartRowMutation,
} from 'Services/cartApi';
import { handleCheckoutClickDatalayerPush } from 'Utils/dataLayerPushService';
import useTrapFocus from 'Hooks/useTrapFocus';

import CtaButton from 'Components/CtaButton';
import CartItemRow from 'Components/CustomerServiceCart/CartItemRow';
import CartItemSummary from 'Components/CustomerServiceCart/CartItemSummary';
import ProductSearch from 'Components/CustomerServiceCart/ProductSearch';
import OrderListing from 'Components/CustomerServiceCart/OrderListing';
import Loader from 'Components/Loader';

import CloseIcon from 'Icons/closeThin.svg?react';
import { Order } from '../Services/orderSearchApi';

interface CustomerServiceCartProps {
    isOpen: boolean;
    setIsOpen: (state: boolean) => void;
}

interface ReturnFocus {
    type: string;
    id: string;
}

const NEW_ORDER_TAB = 'NEW_ORDER';
const EDIT_ORDER_TAB = 'EDIT_ORDER';
const EXCHANGE_ORDER_TAB = 'EXCHANGE_ORDER';

const CustomerServiceCart = ({
    isOpen,
    setIsOpen,
}: CustomerServiceCartProps) => {
    const { shoppingCart, orderDetails, translations, checkoutPage } =
        useSelector((state: IRootState) => state?.cart?.miniCartModel);

    const { variants: cartRowVariantData, loaded: variantsLoaded } =
        useSelector((state: IRootState) => state?.variantData);

    const [returnFocus, setReturnFocus] = useState<ReturnFocus>({
        type: '',
        id: '',
    });
    const [activeTab, setActiveTab] = useState(
        orderDetails?.orderNumber ? EDIT_ORDER_TAB : NEW_ORDER_TAB
    );
    const [loweredQuantityForVariant, setLoweredQuantityForVariant] =
        useState('');
    const initialized = useRef(false);
    const closeTrap = () => {
        setIsOpen(false);
    };
    const trapRef = useTrapFocus(isOpen, closeTrap);

    const [
        updateCartRow,
        {
            isSuccess: isUpdateCartSuccess,
            error: errorCartRow,
            isLoading: isLoadingCartRow,
            isError: isErrorCartRow,
        },
    ] = useUpdateCartRowMutation();
    const [
        addToCart,
        {
            isSuccess: isAddCartSuccess,
            error: errorAddCartRow,
            isLoading: isLoadingAddCartRow,
            isError: isErrorAddCartRow,
        },
    ] = useAddToCartMutation();
    const [
        replaceCartRow,
        {
            isSuccess: isReplaceCartSuccess,
            error: errorReplaceCartRow,
            isLoading: isLoadingReplaceCartRow,
            isError: isErrorReplaceCartRow,
        },
    ] = useReplaceCartMutation();
    const [
        addDiscount,
        {
            isSuccess: isAddDiscountSuccess,
            error: errorAddDiscountRow,
            isLoading: isLoadingAddDiscount,
            isError: isErrorAddDiscountRow,
        },
    ] = useAddDiscountMutation();
    const [
        loadOrderIntoCart,
        {
            isSuccess: isLoadOrderSuccess,
            error: errorLoadOrder,
            isLoading: isLoadingOrder,
            isError: isErrorLoadOrder,
        },
    ] = useLoadOrderIntoCartMutation();
    const [
        unloadOrderFromCart,
        {
            isSuccess: isUnloadOrderSuccess,
            error: errorUnloadOrder,
            isLoading: isLoadingUnloadOrder,
            isError: isErrorUnloadOrder,
        },
    ] = useUnloadOrderFromCartMutation();

    const {
        isLoading: cartIsLoading,
        isError: isErrorCart,
        error: errorCart,
    } = useGetCartQuery();

    const handleCartUpdate = async (
        id: string,
        systemId: string,
        quantity: string,
        type: 'decrement' | 'increment' | 'delete'
    ) => {
        try {
            await updateCartRow({
                articleNumber: id,
                quantity,
                type,
                systemId,
            }).unwrap();

            const customerServiceDiscount = shoppingCart?.orderItems?.find(
                o => o.systemId === systemId
            )?.discount as {
                amount: number;
                discountType: string;
            };

            if (!customerServiceDiscount) return;

            const shouldUpdatePercentDiscount =
                customerServiceDiscount.discountType === 'percent';

            if (shouldUpdatePercentDiscount) {
                await addDiscount({
                    rowSystemId: systemId,
                    value: customerServiceDiscount.amount,
                    type: 'percent',
                }).unwrap();
            }
        } catch (error) {
            console.error(
                'Error while updating cart and applying discount:',
                error
            );
        }
    };

    const handleCartReplace = (
        id: string,
        quantity: number,
        orderRowId: string,
        type: string,
        loweredQuantity: boolean
    ) => {
        replaceCartRow({
            articleNumber: id,
            quantity,
            orderRowSystemId: orderRowId,
        });
        setReturnFocus({ id, type });
        if (loweredQuantity) {
            setLoweredQuantityForVariant(id);
        }
    };

    const handleUpdateDiscount = (
        id: string,
        discountAmount: number,
        discountType: string
    ) => {
        addDiscount({
            rowSystemId: id,
            value: discountAmount,
            type: discountType,
        });
    };

    const handleAddToCart = (id: string) => {
        addToCart({ articleNumber: id, quantity: 1 });
    };

    const handleSelectOrder = async (order: Order) => {
        var result = await loadOrderIntoCart({ orderId: order.orderNumber });

        if (result.data?.impersonationUser?.redirectUrl) {
            location.href = result.data.impersonationUser.redirectUrl;
        }
    };

    const canSwitchAwayFromOrder = () => {
        if (activeTab === NEW_ORDER_TAB || !orderDetails?.orderNumber)
            return true;

        if (
            !window.confirm(
                translations.customerServiceSwitchAwayFromOrder.replace(
                    '{orderNumber}',
                    orderDetails.orderNumber
                )
            )
        ) {
            return false;
        }

        unloadOrderFromCart();
        return true;
    };

    const handleTabSelect = (tab) => {
        if (activeTab === tab || !canSwitchAwayFromOrder()) return;

        setActiveTab(tab);
    };

    useEffect(() => {
        if (isErrorCartRow && 'data' in errorCartRow) {
            const errMessage: { title?: string } = errorCartRow.data;
            toast.error(errMessage.title);
        }
    }, [isErrorCartRow, errorCartRow]);

    useEffect(() => {
        if (isErrorAddCartRow && 'data' in errorAddCartRow) {
            const errMessage: { title?: string } = errorAddCartRow.data;
            toast.error(errMessage.title);
        }
    }, [isErrorAddCartRow, errorAddCartRow]);

    useEffect(() => {
        if (isErrorReplaceCartRow && 'data' in errorReplaceCartRow) {
            const errMessage: { title?: string } = errorReplaceCartRow.data;
            toast.error(errMessage.title);
        }
    }, [isErrorReplaceCartRow, errorReplaceCartRow]);

    useEffect(() => {
        if (isErrorCart && 'data' in errorCart) {
            const errMessage: { title?: string } = errorCart.data;
            toast.error(errMessage.title);
        }
    }, [isErrorCart, errorCart]);

    useEffect(() => {
        if (isErrorLoadOrder && 'data' in errorLoadOrder) {
            const errMessage: { title?: string } = errorLoadOrder.data;
            toast.error(errMessage.title || 'Error loading order');
        }
    }, [isErrorLoadOrder, errorLoadOrder]);

    useEffect(() => {
        if (isErrorUnloadOrder && 'data' in errorUnloadOrder) {
            const errMessage: { title?: string } = errorUnloadOrder.data;
            toast.error(errMessage.title || 'Error unloading order');
        }
    }, [isErrorUnloadOrder, errorUnloadOrder]);

    useEffect(() => {
        if (shoppingCart && !initialized.current) {
            initialized.current = true;

            if (orderDetails?.originalOrderNumber) {
                setActiveTab(EXCHANGE_ORDER_TAB);
            } else if (orderDetails?.orderNumber) {
                setActiveTab(EDIT_ORDER_TAB);
            }
        }
    }, [shoppingCart]);

    return (
        <section
            ref={trapRef}
            className="flex h-[100dvh] w-full flex-col bg-white shadow-lg min-[550px]:w-[530px] min-[550px]:max-w-[530px] min-[1000px]:w-[700px] min-[1000px]:max-w-[700px]"
        >
            {cartIsLoading && (
                <div className="flex h-full flex-1 flex-col justify-center">
                    <Loader />
                </div>
            )}
            {!cartIsLoading && (
                <>
                    <div className="flex bg-white">
                        <button
                            className={`flex-1 border-r border-gray-200 px-4 py-3 text-sm font-medium text-gray-900 transition-colors ${
                                activeTab === NEW_ORDER_TAB
                                    ? 'bg-[#d7a8b1]'
                                    : 'bg-white hover:bg-[#e8d4da]'
                            }`}
                            onClick={handleTabSelect.bind(null, NEW_ORDER_TAB)}
                        >
                            {translations.customerServiceNewOrderTab}
                        </button>
                        <button
                            className={`flex-1 border-r border-gray-200 px-4 py-3 text-sm font-medium text-gray-900 transition-colors ${
                                activeTab === EDIT_ORDER_TAB
                                    ? 'bg-[#d7a8b1]'
                                    : 'bg-white hover:bg-[#e8d4da]'
                            }`}
                            onClick={handleTabSelect.bind(null, EDIT_ORDER_TAB)}
                        >
                            {translations.customerServiceEditOrderTab}
                        </button>
                        <button
                            className={`flex-1 px-4 py-3 text-sm font-medium text-gray-900 transition-colors ${
                                activeTab === EXCHANGE_ORDER_TAB
                                    ? 'bg-[#d7a8b1]'
                                    : 'bg-white hover:bg-[#e8d4da]'
                            }`}
                            onClick={handleTabSelect.bind(null, EXCHANGE_ORDER_TAB)}
                        >
                            {translations.customerServiceExchangeOrderTab}
                        </button>
                    </div>
                    {activeTab === EDIT_ORDER_TAB &&
                        !orderDetails?.orderNumber && (
                            <OrderListing selectOrder={handleSelectOrder} />
                        )}
                    {activeTab === EXCHANGE_ORDER_TAB &&
                        !orderDetails?.orderNumber && (
                            <OrderListing
                                selectOrder={handleSelectOrder}
                                returnOrders={true}
                            />
                        )}
                    {(activeTab === NEW_ORDER_TAB || orderDetails?.orderNumber) && (
                        <>
                            <div className="relative shadow-[0px_5px_16px_0px_rgba(0,0,0,0.08)]">
                                <div className="flex items-center justify-between px-7 py-4">
                                    <h3 className="my text-l flex flex-wrap font-montserrat font-semibold">
                                        {translations.miniCartTitle} (
                                        {shoppingCart.totalQuantity}
                                        {shoppingCart.totalQuantity !== 1
                                            ? ` ${translations.cartTitleAdditionalInfoPlural}`
                                            : ` ${translations.cartTitleAdditionalInfoSingular}`}
                                        )
                                    </h3>
                                    <button
                                        className="cursor-pointer"
                                        onClick={() => setIsOpen(false)}
                                    >
                                        <CloseIcon className="h-5 w-5" />
                                        <span className="sr-only">
                                            {translations.minicartClose}
                                        </span>
                                    </button>
                                </div>
                                {orderDetails?.orderNumber && !orderDetails.originalOrderNumber && (
                                    <div className="bg-[#ff9e42] p-1 text-center font-medium">
                                        {translations.customerServiceCurrentOrder
                                            ?.replace(
                                                '{orderNumber}',
                                                orderDetails.orderNumber
                                            )}
                                    </div>
                                )}
                                {orderDetails?.orderNumber && orderDetails.originalOrderNumber && (
                                    <div className="bg-[#ff9e42] p-1 text-center font-medium">
                                        {translations.customerServiceCurrentExchangeOrder
                                            ?.replace(
                                                '{orderNumber}',
                                                orderDetails.orderNumber
                                            )
                                            ?.replace(
                                                '{originalOrderNumber}',
                                                orderDetails.originalOrderNumber
                                            )}
                                    </div>
                                )}
                                <div>
                                    <ProductSearch
                                        addToCart={handleAddToCart}
                                    />
                                </div>
                                {shoppingCart.totalQuantity > 0 && (
                                    <div className="flex bg-[#F8F5F0] text-xs">
                                        <span className="grow py-1.5 pl-4">
                                            {
                                                translations.customerServiceCartProduct
                                            }
                                        </span>
                                        <span className="w-44 py-1.5 pl-4">
                                            {
                                                translations.customerServiceCartVariant
                                            }
                                        </span>
                                        <span className="w-28 py-1.5 pl-4">
                                            {translations.cartPriceLabel}
                                        </span>
                                        <span className="w-20 py-1.5 pr-4 text-right">
                                            {translations.cartTotalPriceLabel}
                                        </span>
                                    </div>
                                )}
                            </div>
                            {shoppingCart.totalQuantity > 0 ? (
                                <ul
                                    className={`scroll-bar-gsd::-webkit-scrollbar scroll-bar-gsd::-webkit-scrollbar-thumb scroll-bar-gsd flex grow flex-col gap-1 overflow-y-auto bg-[#F8F5F0] transition-opacity duration-layout ${
                                        isLoadingCartRow || isLoadingAddCartRow
                                            ? 'opacity-50'
                                            : 'opacity-100'
                                    }`}
                                >
                                    {variantsLoaded &&
                                        shoppingCart?.orderItems.map(item => (
                                            <CartItemRow
                                                key={`row-${item.systemId}`}
                                                updateCartRow={handleCartUpdate}
                                                replaceCartRow={
                                                    handleCartReplace
                                                }
                                                updateDiscount={
                                                    handleUpdateDiscount
                                                }
                                                orderItem={item}
                                                variantSelectData={
                                                    cartRowVariantData[
                                                        item.systemId as keyof RowSpecificVariantSelects
                                                    ]
                                                }
                                                translations={translations}
                                                returnFocus={
                                                    returnFocus.id === item.id
                                                        ? returnFocus.type
                                                        : ''
                                                }
                                                showLoweredQuantityWarning={
                                                    loweredQuantityForVariant ===
                                                    item.id
                                                }
                                            />
                                        ))}
                                </ul>
                            ) : (
                                <div className="flex grow items-center justify-center bg-[#F8F5F0]">
                                    {translations.customerServiceCartEmpty}
                                </div>
                            )}
                            <div className="w-full bg-white px-4.5 py-2.5 shadow-[0px_-5px_16px_0px_rgba(0,0,0,0.05)]">
                                <CartItemSummary
                                    isLoading={
                                        isLoadingCartRow || isLoadingAddCartRow
                                    }
                                    items={orderDetails}
                                    translations={translations}
                                />

                                <CtaButton
                                    as="a"
                                    ctaType="primary"
                                    className="checkout-button w-full text-center font-semibold"
                                    href={checkoutPage.href}
                                    onClick={() =>
                                        handleCheckoutClickDatalayerPush()
                                    }
                                >
                                    {checkoutPage.text}
                                </CtaButton>
                            </div>
                        </>
                    )}
                </>
            )}
        </section>
    );
};

export default CustomerServiceCart;
